import { isElementCategoryID } from '../helpers/element-category.helpers';
import { isCloseTo } from '../helpers/math.helpers';
import { getMissingProperties } from '../helpers/object.helpers';
import {
  getProductSourceFromId,
  isCustomProductId,
  isBoverketProductId,
  isOekobaudatProductId,
  isNodonProductId,
  getOriginalProductId,
  isCo2DataFiProductId,
} from '../helpers/product.helpers';
import { makeSentence } from '../helpers/string.helpers';
import { isConversionFactorQuantityUnit } from '../helpers/unit.helpers';
import {
  IProduct,
  IFactoryProduct,
  REQUIRED_PRODUCT_PROPERTIES,
  CUSTOM_ID_PREFIX,
  ProductSources,
  ProductID,
} from '../models/product.model';
import { throwValidationErrors } from './validation.helpers';
import { GenericValidationError, ValidationResult } from './validation.models';

/**
 * Validate product
 * @param product
 * @param verbose
 * @returns string for error or true for valid
 */
export const isValidProduct = (
  product: IProduct | IFactoryProduct | undefined,
): ValidationResult => {
  if (!product) {
    return new GenericValidationError('Product is not defined');
  }

  const missingProperties = getMissingProperties(
    product,
    ...REQUIRED_PRODUCT_PROPERTIES,
  );

  if (missingProperties.length) {
    return new GenericValidationError(
      'Product is missing properties: ' + makeSentence(...missingProperties),
    );
  }
  const {
    id,
    name,
    source,
    categories,
    organizations,
    unit,
    generic_id,
    category_property_value_record = {},
    conversion_factors,
    category_id,
  } = product as IProduct;
  const idSource = getProductSourceFromId(id);

  if (generic_id) {
    if (!isCustomProductId(id)) {
      return new GenericValidationError(
        'Product has a generic_id but is not a custom product',
      );
    }
    const validId = isValidGenericProductId(generic_id);
    if (validId !== true) {
      return validId;
    }
  }

  if (Object.keys(category_property_value_record).length && !category_id) {
    return new GenericValidationError(
      'Product has category_property_value_record but no category_id',
    );
  }

  if (category_id && !isElementCategoryID(category_id)) {
    return new GenericValidationError('Product has invalid category_id');
  }

  if (!name) {
    return new GenericValidationError('Product has no name');
  }

  if (idSource !== source) {
    return new GenericValidationError('Product id and source is not matching');
  }

  if (!isConversionFactorQuantityUnit(unit)) {
    return new GenericValidationError('Product has invalid unit ' + unit);
  }

  if (!isCloseTo(conversion_factors[unit], 1)) {
    return new GenericValidationError(
      'Selected unit is not 1 in conversion factors',
    );
  }

  // Only for custom products
  if (source === ProductSources.Custom) {
    if (!organizations?.length) {
      return new GenericValidationError('Custom product has no organizations');
    }
    if (categories.Custom !== true) {
      return new GenericValidationError(
        'Custom product has no custom category',
      );
    }
  }

  // const validConversionFactors = isValidConversionFactors(conversion_factors);

  // if (validConversionFactors !== true) {
  //   return validConversionFactors;
  // }

  return isValidProductId(id);
};

/**
 * Validate product id
 * @param id
 * @returns true if valid, otherwise an error message
 */
const isValidProductId = (id: unknown): ValidationResult => {
  if (typeof id !== 'string') {
    return new GenericValidationError('Product id is not a string');
  }
  if (
    !isOekobaudatProductId(id) &&
    !isBoverketProductId(id) &&
    !isCustomProductId(id) &&
    !isNodonProductId(id) &&
    !isCo2DataFiProductId(id)
  ) {
    return new GenericValidationError('Invalid product id prefix');
  }
  const suffix = getOriginalProductId(id).replace(CUSTOM_ID_PREFIX, '');
  if (suffix.length < 7) {
    return new GenericValidationError('Invalid product id suffix');
  }
  return true;
};

export const validateProduct = (
  product: IProduct | IFactoryProduct | undefined,
  onError?: (error: string) => void,
): IProduct => {
  throwValidationErrors(
    isValidProduct(product),
    'ProductValidationError',
    onError,
  );
  return product as IProduct;
};

export const validateGenericProductId = (
  id: string | number | undefined,
): ProductID => {
  throwValidationErrors(isValidGenericProductId(id), 'ProductValidationError');
  return id as ProductID;
};

export const isValidGenericProductId = (
  id: string | number | undefined,
): ValidationResult => {
  if (isValidProductId(id) !== true || isCustomProductId(id as string)) {
    return new GenericValidationError('Invalid generic product id');
  }

  return true;
};
